<template>
  <div class="cg-phone-input" :style="styleVariables">
    <label v-if="showLabel" :for="elementId">
      <!-- @slot Adds input label. -->
      <slot name="label" />
    </label>

    <div
      :aria-invalid="hasError"
      aria-describedby="errmsg"
      class="cg-phone-input__content"
    >
      <div
        class="cg-phone-input__menu-wrapper"
        ref="wrapper"
        :class="{ 'cg-phone-input__menu-wrapper--disabled': disabled }"
        tabindex="0"
        @keydown="onMenuKeyPress"
        @keydown.space="showMenu = true"
        @keydown.tab="showMenu = false"
        @keydown.esc="showMenu = false"
      >
        <div
          class="cg-phone-input__selected"
          role="button"
          aria-haspopup="listbox"
          :aria-owns="`list-${elementId}`"
          :aria-expanded="showMenu ? 'true' : 'false'"
          @click="onSelectClick"
        >
          <span :class="['vti__flag', selectedCountry.iso2.toLowerCase()]" />
          +{{ selectedCountry.dialCode }}

          <div class="cg-phone-input__menu-icon">
            <cg-icon-select-chevron :width="16" :height="16" />
          </div>
        </div>

        <div
          v-show="showMenu"
          :id="`list-${elementId}`"
          class="cg-phone-input__menu"
          role="listbox"
          tabindex="-1"
          ref="selectMenu"
          :class="{
            'cg-phone-input__menu--top': menuProps.top,
            'cg-phone-input__menu--bottom': menuProps.bottom,
            'cg-phone-input__menu--align-left': menuProps.alignLeft,
            'cg-phone-input__menu--align-right': menuProps.alignRight,
          }"
        >
          <div
            v-for="(country, index) in availableCountries"
            :key="`list-item-${elementId}-${index}`"
            :id="`list-item-${elementId}-${index}`"
            class="cg-phone-input__menu-item"
            :class="{ 'cg-phone-input__menu-item--active': country.iso2 === selectedCountry.iso2 }"
            role="option"
            tabindex="-1"
            :aria-selected="country.iso2 === selectedCountry.iso2"
            @click="selectCountry(country)"
            @mousemove="selectedIndex = index"
            @keypress.enter.prevent="selectedIndex = index"
          >
            <span :class="['vti__flag', country.iso2.toLowerCase()]" />

            {{ country.name }}

            +{{ country.dialCode }}
          </div>
        </div>
      </div>

      <input
        v-model="lazyValue"
        type="tel"
        :disabled="disabled"
        :id="elementId"
        :placeholder="placeholder"
        maxlength="19"
        @focus="onFocus"
        @blur="onBlur"
        @input="onInput"
      >
    </div>

    <div v-if="showDetails">
      <div v-if="showHintMessage" class="cg-phone-input__hint-message">
        <!-- @slot Adds an item with hint styles. -->
        <slot name="hint-message" />
      </div>
      <div v-if="showErrorMessage" class="cg-phone-input__error-message" id="errmsg">
        <!-- @slot Adds an item with error styles. -->
        <slot name="error-message">
          {{ getFirstErrorMessage }}
        </slot>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { PropType } from 'vue';
import ElementId from '../../mixins/elementId';
import allCountries from './assets/countriesList';
import { parsePhoneNumberFromString } from 'libphonenumber-js';

// @ts-ignore
import { CgIconSelectChevron } from '../../icons'

import type { CountryCode, PhoneNumber } from 'libphonenumber-js';
import type ValidationMethod from '@corporategift/design-system/validations/ValidationMethod';

type IPhoneInputSlots = {
  'hint-message'?: unknown;
  'error-message'?: unknown;
  'label'?: unknown;
}

type CountryItem  = {
  iso2: CountryCode;
  dialCode: string;
  name: string;
  priority?: number;
  areaCodes?: Array<string>;
}

type PhoneObject = PhoneNumber & {
  countryCode: CountryCode;
  valid: boolean;
  formatted: string;
  country: CountryItem;
}

export default ElementId.extend({
  name: 'CgPhoneInput',
  components: { CgIconSelectChevron },
  props: {
    /**
     * The input’s value
     * @model
     */
    value: {
      type: String,
      required: false,
      default: null,
    },
    /**
     * Sets the DOM id on the component
     */
    id: {
      type: String,
      required: false,
      default: null,
    },
    /**
     * Removes the ability to click or target the component.
     */
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * Hides hint and validation errors.
     */
    hideDetails: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * Sets the height of the input
     */
    height: {
      type: Number,
      required: false,
      default: 40,
    },
    /**
     * Sets the max-width of the input
     */
    width: {
      type: Number,
      required: false,
      default: null,
    },
    /**
     * Sets the max-height of the menu
     */
    menuHeight: {
      type: Number,
      required: false,
      default: 300,
    },
    /**
     * Align menu to **left** or **right** - it's useful when menu can be wider than the input and overflow it
     */
    align: {
      type: String as PropType<'left' | 'right'>,
      required: false,
      default: 'left',
    },
    /**
     * Sets the select menu to be render always on top
     */
    top: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * Sets the select menu to be render always on bottom
     */
    bottom: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * Sets the input’s placeholder text
     */
    placeholder: {
      type: String,
      required: false,
      default: null,
    },
    /**
     * Array of functions
     * @see list of available validations
     */
    validation: {
      type: Array as PropType<Array<ValidationMethod>>,
      required: false,
      default: () => ([]),
    },
    /**
     * Delays validation until blur event
     */
    validateOnBlur: {
      type: Boolean,
      required: false,
      default: true,
    },
    /**
     * Puts the input in a manual error state
     */
    error: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data: () => ({
    lazyValue: '',
    activeCountryCode: 'US' as CountryCode,
    inputIsValid: true,
    isFocused: false,
    showMenu: false,
    errorMessages: [] as Array<string>,
    availableCountries: allCountries as Array<CountryItem>,
    menuProps: {
      top: false,
      bottom: false,
      alignLeft: false,
      alignRight: false,
    },
    // helpers for typing when menu is open
    selectedIndex: 0 as number,
    typeToFindInput: '',
    typeToFindTimer: null as number | null,
  }),
  computed: {
    modelValue: {
      get(): string | number | null { return this.value },
      set(val: string | number | null) {
        /**
         * Emitted when the v-model is changed.
         * 
         * @property {string} value new value
         */
        this.$emit('input', val);
      }
    },
    styleVariables(): Object {
      const { height, width, menuHeight } = this;

      return {
        '--input-height': `${height}px`,
        '--input-width': width ? `${width}px` : undefined,
        '--select-menu-height': menuHeight ? `${menuHeight}px` : undefined,
      };
    },
    getFirstErrorMessage(): string | null {
      const { errorMessages } = this

      return errorMessages.length > 0 ? errorMessages[0] : null;
    },
    hasInternalError (): boolean {
      return this.errorMessages.length > 0 || !this.inputIsValid;
    },
    hasError (): boolean {
      return this.hasInternalError || this.error;
    },
    slots(): IPhoneInputSlots {
      return this.$slots;
    },
    showLabel(): boolean {
      return !!this.slots?.label;
    },
    showDetails(): boolean {
      const { slots, errorMessages, hideDetails } = this;

      if (hideDetails) return false;

      return !!(slots?.['hint-message'] || slots?.['error-message'] || errorMessages.length);
    },
    showHintMessage(): boolean {
      return !!this.slots?.['hint-message'];
    },
    showErrorMessage(): boolean {
      return !!(this.slots?.['error-message'] || this.errorMessages.length);
    },
    selectedCountry(): CountryItem | undefined {
      const { activeCountryCode, availableCountries } = this;

      return availableCountries.find((country: CountryItem) => country.iso2 === activeCountryCode);
    },
    phoneObject (): PhoneObject | null {
      const { activeCountryCode, lazyValue } = this

      let result: PhoneNumber | {} = {};

      const isPhoneNumber = (result: PhoneNumber | {}): result is PhoneNumber => {
        return (result as PhoneNumber).isValid !== undefined;
      }

      if (lazyValue?.at(0) === '+') {
        result = parsePhoneNumberFromString(lazyValue) || {}
      } else {
        result = parsePhoneNumberFromString(lazyValue, activeCountryCode) || {}
      }

      if (!isPhoneNumber(result)) { return null }

      let isValid = result.isValid() ?? false
      let formatted = lazyValue;

      if (isValid) { formatted = result.format('NATIONAL') }

      Object.assign(result, {
        countryCode: result?.country,
        valid: isValid,
        country: this.selectedCountry,
        formatted,
      })

      return result as PhoneObject
    },
    phoneObjectCountryCode (): CountryCode | null {
      if (this.phoneObject) { return this.phoneObject.countryCode }
      return null
    },
    phoneObjectIsValid (): boolean | null {
      if (this.phoneObject) { return this.phoneObject.valid }
      return null
    },
    phoneObjectFormatted (): string | null {
      if (this.phoneObject) { return this.phoneObject.formatted }
      return null
    },
    phoneObjectNumber (): string | null {
      if (this.phoneObject) { return this.phoneObject.number }
      return null
    }
  },
  watch: {
    // value (val: any) { this.lazyValue = val || '' },
    lazyValue (val: any, oldValue: any) {
      const isValueValid = /^[()\-+0-9\s]*$/.test(val);

      if (!isValueValid) {
        this.$nextTick(() => {
          this.lazyValue = oldValue;
        })
      }
    },
    showMenu (val: boolean) {
      if (val) { this.setMenuProps(); }
    },
    hasInternalError (val: boolean) {
      /**
       * The `error.sync` event - returns boolean
       * 
       * @property {boolean} val
       */
      this.$emit('update:error', val);
    },
    phoneObjectCountryCode (val: CountryCode | null) {
      if (val) { this.activeCountryCode = val }
    },
    phoneObjectIsValid (val: boolean) {
      if (val !== null && !this.validateOnBlur) {
        this.inputIsValid = val;
      }
    },
    phoneObjectFormatted (val: string | null) {
      if (val) {
        this.lazyValue = val

        this.$nextTick(() => {
          if (val !== this.modelValue) {
            this.lazyValue = val
          }
        })
      }
    },
    isFocused (val: boolean) {
      if (!val) { this.updateModelValue() }
    }
  },
  mounted() {
    if (this.value) { this.lazyValue = this.value.trim() }

    this.selectedIndex = this.availableCountries
      .findIndex((country) => country.iso2 === this.activeCountryCode) || 0

    this.$nextTick(() => {
      document.addEventListener('click', this.clickOutsideHandler)
    });
  },
  beforeDestroy () {
    document.removeEventListener('click', this.clickOutsideHandler)
  },
  methods: {
    onFocus(e: FocusEvent): void {
      this.isFocused = true;
      /**
       * Emitted when component is focused
       * 
       * @property {FocusEvent} Event
       */
      this.$emit('focus', e);
    },
    onBlur(e: FocusEvent): void {
      const { phoneObjectIsValid } = this

      this.isFocused = false;
      if (this.validateOnBlur) {
        this.validate();
        if (phoneObjectIsValid !== null) { this.inputIsValid = phoneObjectIsValid; }
      }
      /**
       * Emitted when the input is blurred
       * 
       * @property {FocusEvent} Event
       */
      this.$emit('blur', e);
    },
    onInput(e: InputEvent): void {
      if (!this.validateOnBlur) { this.validate(); }
    },
    onMenuKeyPress(e: KeyboardEvent): void {
      const { typeToFindTimer, availableCountries, selectedIndex } = this;
      if (!this.showMenu) { return; }
      
      if (e.code === 'Enter') {
        if (selectedIndex !== null) {
          this.selectCountry(availableCountries[selectedIndex]);
        }
      } else if (e.code === 'ArrowUp') {
        e.preventDefault();

        if (selectedIndex !== null) {
          this.selectedIndex = Math.max(0, selectedIndex - 1);

          const selectedCountry: Element = (this.$refs.selectMenu as HTMLDivElement)
            ?.children[this.selectedIndex];

          (selectedCountry as HTMLDivElement).focus()

          if (this.$refs.selectMenu) {
            (this.$refs.selectMenu as HTMLDivElement).scrollTop =
              (selectedCountry as HTMLDivElement).offsetTop;
          }
        }
      } else if (e.code === 'ArrowDown') {
        e.preventDefault();

        if (selectedIndex !== null) {
          this.selectedIndex = Math.min(availableCountries.length - 1, selectedIndex + 1);

          const selectedCountry: Element = (this.$refs.selectMenu as HTMLDivElement)
            ?.children[this.selectedIndex];

          (selectedCountry as HTMLDivElement).focus()

          if (this.$refs.selectMenu) {
            (this.$refs.selectMenu as HTMLDivElement).scrollTop =
              (selectedCountry as HTMLDivElement).offsetTop;
          }
        }
      } else if (!['Escape', 'Tab', 'ShiftLeft', 'ShiftRight'].includes(e.code)) {
        this.typeToFindInput += e.key;
        if (typeToFindTimer) { clearTimeout(typeToFindTimer); }

        this.typeToFindTimer = setTimeout(() => {
          this.typeToFindInput = '';
        }, 700);

        const typedCountryIndex = availableCountries
          .findIndex((country) => country.name.toLowerCase().startsWith(this.typeToFindInput));

        if (typedCountryIndex >= 0) {
          this.selectedIndex = typedCountryIndex;
          const selectedCountry: Element = (this.$refs.selectMenu as HTMLDivElement)
            ?.children[typedCountryIndex];

          if (this.$refs.selectMenu) {
            (this.$refs.selectMenu as HTMLDivElement).scrollTop =
              (selectedCountry as HTMLDivElement).offsetTop;
          }
        }
      }
    },
    updateModelValue () {
      const { phoneObjectNumber, lazyValue } = this;
      this.modelValue = phoneObjectNumber || lazyValue;
    },
    setMenuProps (): void {
      const { align, top, bottom } = this

      this.menuProps.alignLeft = align === 'left';
      this.menuProps.alignRight = align === 'right';

      if (top || bottom) {
        this.menuProps.top = top || false;
        this.menuProps.bottom = bottom || false;
      } else {
        const wrapperRect = (this.$refs.wrapper as Element)?.getBoundingClientRect();
        const windowHeight = window?.innerHeight ?? 1000;

        this.$nextTick(() => {
          const { height } = (this.$refs.selectMenu as Element)?.getBoundingClientRect();
          const { top, bottom } = wrapperRect;
          const wrapperBottom = windowHeight - bottom;

          this.menuProps.bottom = (wrapperBottom >= height) || (height > top);
          this.menuProps.top = (height > wrapperBottom) && (top >= height);
        })
      }
    },
    clickOutsideHandler (e: MouseEvent): void {
      const { target } = e;
      const { wrapper } = this.$refs;

      const targetClassName = (target as Element)?.className;
      const targetId = (target as Element)?.attributes?.getNamedItem('0');
      const parentTargetId = (wrapper as Element)?.attributes?.getNamedItem('0');

      const elementsWhiteList = ['cg-phone-input__selected'];

      if (wrapper && !(wrapper as Element)?.contains(target as Node)) {
        if (!elementsWhiteList.includes(targetClassName) && targetId === parentTargetId) {
          this.showMenu = false;
        }
      }
    },
    onSelectClick (e: FocusEvent): void {
      this.showMenu = !this.showMenu;

      this.$nextTick(() => {
        this.scrollToSelectedElement()
      })
    },
    scrollToSelectedElement (): void {
      if (this.$refs.selectMenu) {
        const activeElement: HTMLDivElement | null = (this.$refs.selectMenu as HTMLDivElement)
          .querySelector('.cg-phone-input__menu-item--active')

        if (activeElement) {
          (this.$refs.selectMenu as HTMLDivElement).scrollTop = activeElement.offsetTop
        }
      }
    },
    selectCountry (country: CountryItem): void {
      this.activeCountryCode = country.iso2;
      this.showMenu = false;
    },
    /**
     * Validates input. Returns **true** if successful and **false** if not.
     * @public
     */
    validate (): boolean {
      const { phoneObjectIsValid } = this;
      const value = this.lazyValue;
      const errors: Array<string | boolean> = [];

      if (phoneObjectIsValid === false) { errors.push('Invalid phone number, please update') }

      (this.validation as Array<ValidationMethod>).forEach((fnc) => {
        const valid = fnc(value);

        if (valid === false || typeof valid === 'string') {
          errors.push(valid || '');
        } else if (typeof valid !== 'boolean') {
          console.error(`Validation should return a string or boolean, received '${typeof valid}' instead`);
        }
      });

      this.inputIsValid = errors.length === 0;

      if (phoneObjectIsValid !== null && errors.length === 0) { this.inputIsValid = phoneObjectIsValid; }
      this.errorMessages = (errors?.filter((item) => ((typeof item === 'string') && item)) ?? []) as Array<string>;

      return !this.hasError;
    },
    /**
     * Resets validation.
     * @public
     */
    resetValidation (): void {
      this.inputIsValid = true;
      this.errorMessages = [];
    },
  },
});
</script>

<style lang="scss" scoped>
@import './styles.scss';
</style>
